'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import type { GetDefiYieldsOverviewResponse } from '@flipster/apis/v1/dynamic-earn/types';
import { FormattedMessage } from '@flipster/i18n';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import Num from '@flipster/utils/num';

import { defiYieldsOverviewBunja } from '../../../shared/state';
import { ComingSoonCard } from './comming-soon-card';
import { NarsilEarningsCard } from './narsil-card';

type ProductKey = 'NRSUSDTP';

const productKeys: readonly ProductKey[] = ['NRSUSDTP'];

const isProductKey = (key: string): key is ProductKey => productKeys.includes(key as ProductKey);

const isSubscribed = (product?: { subscribedBalance?: string; processingMintRequests?: unknown[] }) =>
  Num(product?.subscribedBalance ?? '0').gte('0.01') || (product?.processingMintRequests ?? []).length > 0;

const getProductOverview = (overview: GetDefiYieldsOverviewResponse | undefined, key: ProductKey) => overview?.[key];

const productCardMap: Record<ProductKey, React.ComponentType> = {
  NRSUSDTP: NarsilEarningsCard,
};

const productPriority: Record<ProductKey, number> = { NRSUSDTP: 0 };

export const Earnings = () => {
  const { overviewAtom, productOrderAtom, nrsusdtpAvailableAtom } = useBunja(defiYieldsOverviewBunja);
  const overview = useAtomValue(overviewAtom);
  const productOrder = useAtomValue(productOrderAtom);
  const nrsusdtpAvailable = useAtomValue(nrsusdtpAvailableAtom);

  const availabilityMap: Record<ProductKey, boolean> = {
    NRSUSDTP: nrsusdtpAvailable,
  };

  const products = productOrder.length > 0 ? productOrder.filter(isProductKey) : [...productKeys];

  const sortedProducts = [...products].sort((a, b) => {
    const subscribedDiff =
      Number(isSubscribed(getProductOverview(overview, b))) - Number(isSubscribed(getProductOverview(overview, a)));
    if (subscribedDiff !== 0) return subscribedDiff;
    return productPriority[a] - productPriority[b];
  });

  return (
    <section className={css({ mt: 64 })}>
      <div className={stack({ gap: { tabletDown: 20, tablet: 24 } })}>
        <h2
          className={css({
            textStyle: 'Title/L',
            color: 'content.default.level-5',
          })}
        >
          <FormattedMessage id='Earn.r260319.Dynamic_earn_products' />
        </h2>
        <div className={stack({ gap: { base: 10, tablet: 20 } })}>
          {sortedProducts.map((productKey) => {
            if (!availabilityMap[productKey]) return null;
            const Card = productCardMap[productKey];
            if (!Card) return null;
            return <Card key={productKey} />;
          })}
          <ComingSoonCard />
        </div>
      </div>
    </section>
  );
};
